import React, { createContext, useContext, useEffect, useState } from "react";
import { api, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared storefront branding/rates/categories — fetched ONCE per session at the App root
// (not per-page) so navigating between pages never re-fetches or re-flickers the logo.
const SiteContentContext = createContext(null);

export const SiteContentProvider = ({ children }) => {
  const { shopLoggedIn, adminLoggedIn } = useAuth();
  const [content, setContent] = useState({});
  const [goldRate, setGoldRate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // `/categories` (and the catalog generally) is gated behind login — B2B wholesale pricing/
  // catalog is confidential and never shown to anonymous visitors. This provider mounts at the
  // App root, which happens BEFORE any login completes, so the very first fetch always 401s and
  // leaves `categories` empty. Re-running the fetch whenever login state flips (either shop OR
  // admin/staff signing in — or out) is what actually populates it once a session exists,
  // instead of it staying permanently empty for the rest of the tab's lifetime.
  useEffect(() => {
    Promise.allSettled([
      api.get("/homepage/public"),
      api.get("/rates/public"),
      api.get("/categories"),
    ]).then(([homeRes, rateRes, catRes]) => {
      if (homeRes.status === "fulfilled") {
        const c = homeRes.value.data || {};
        setContent(c);
        // Warm the logo fetch/decode the moment its URL is known so the header logo
        // paints immediately once BrandLogo mounts (no late swap-in from a cold fetch).
        if (c.logo_image) {
          const warm = new Image();
          warm.src = resolveImg(c.logo_image);
          if (warm.decode) warm.decode().catch(() => {});
        }
      }
      if (rateRes.status === "fulfilled") setGoldRate(rateRes.value.data?.gold_rate_per_gram_24k ?? null);
      setCategories(catRes.status === "fulfilled" ? (catRes.value.data || []) : []);
    }).finally(() => setLoaded(true));
  }, [shopLoggedIn, adminLoggedIn]);

  return (
    <SiteContentContext.Provider value={{ content, goldRate, categories, loaded }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within a SiteContentProvider");
  return ctx;
};
